import { Button, ConfigProvider, Divider, Input, InputNumber, Menu, Modal, Select, Switch, Tabs, Tag, TimePicker, Tooltip, Typography, theme } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { API_DOMAIN } from '../constants';
import useSnackbar from '../Snackbar/UseSnackbar';
import {
    BellOutlined, DeleteOutlined, DollarOutlined, EyeOutlined, FileDoneOutlined,
    LinkOutlined, MessageOutlined, PictureOutlined, PlusOutlined, RobotOutlined,
    SafetyCertificateOutlined, SaveOutlined, SwapOutlined, ToolOutlined,
} from '@ant-design/icons';
import { SettingsTab as ApprovalSettingsTab } from '../approvals/settings/SettingsTab';
import { ChatSettingsPanel } from './ChatSettingsPanel';
import { MarginRulesPanel } from './MarginRulesPanel';
import { UpdateNotificationPanel } from './UpdateNotificationPanel';
import { WaitingPriceReportSchedulerPanel } from './WaitingPriceReportSchedulerPanel';
import { ZohoConnectionPanel } from './ZohoConnectionPanel';
import { useAuth } from '../contexts/AuthContext';
import { FEATURE_KEYS, fetchFeatureVisibility, saveFeatureVisibility } from '../qv4/featureVisibility';
import AccessPresetsPanel from './AccessPresetsPanel';
import { useAccess } from '../../features/access/context/AccessProvider';

// Add new items anywhere in this array — position here doesn't matter.
// UPDATE_NOTIFICATION_ID is pinned last by orderedSettingsOptions below,
// no matter where it's declared or what gets added later.
const UPDATE_NOTIFICATION_ID = 9;
const settingsOptions = [
    { id: 2, name: "WATI Round-Robin", icon: <SwapOutlined />, permission: "settings.sales_assignment.manage" },
    // Listed 2nd (rather than appended last) so it's immediately visible in
    // the menu pane without scrolling — it was the 10th/last item before,
    // easy to miss in the fixed-height modal. Kept out of the very first
    // slot deliberately: settingsOptions[0] is also the default-selected
    // tab (see `useState(settingsOptions[0].id)` below) and Round-Robin
    // should stay that default for everyone else already used to it.
    { id: 15, name: "Operations Round-Robin", icon: <ToolOutlined />, permission: "settings.operations_assignment.manage" },
    { id: 14, name: "Margin Rules", icon: <DollarOutlined />, permission: "settings.margin_rules.manage" },
    { id: 3, name: "Query Images Watermark", icon: <PictureOutlined />, permission: "settings.query_images.manage" },
    { id: 7, name: "Approval Settings", icon: <SafetyCertificateOutlined />, permission: "settings.approvals.manage" },
    { id: 8, name: "Chat Settings", icon: <MessageOutlined />, permission: "settings.chat.manage" },
    { id: 9, name: "Update Notification", icon: <BellOutlined />, permission: "settings.release_notifications.manage" },
    { id: 10, name: "Feature Visibility", icon: <EyeOutlined />, permission: "settings.feature_visibility.manage" },
    { id: 11, name: "AI Image Analysis", icon: <RobotOutlined />, permission: "settings.ai_media_analysis.manage" },
    { id: 12, name: "Waiting Price Reports", icon: <FileDoneOutlined />, permission: "settings.waiting_price_reports.manage" },
    { id: 13, name: "Zoho Connection", icon: <LinkOutlined />, permission: "settings.zoho.manage" },
    { id: 16, name: "Access Presets", icon: <SafetyCertificateOutlined />, permission: "settings.access_presets.manage" },
];
// Array.prototype.sort is stable, so this only ever moves Update
// Notification to the end — every other item keeps its declared order.
const orderedSettingsOptions = [...settingsOptions].sort((a, b) => {
    if (a.id === UPDATE_NOTIFICATION_ID) return 1;
    if (b.id === UPDATE_NOTIFICATION_ID) return -1;
    return 0;
});

const MODAL_WIDTH = 960;
const AI_MEDIA_ANALYSIS_MODE = "ai_media_analysis_config";

function PanelHeader({ title, extra }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>{title}</Typography.Title>
            {extra}
        </div>
    );
}

const SettingsModal = ({ open_settings, handleCloseSettingsModal }) => {

    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar, ToastContainer } = useSnackbar();
    const { authState } = useAuth();
    const { can } = useAccess();
    const { token } = theme.useToken();

    const [selectedOption, setSelectedOption] = useState(settingsOptions[0].id);
    const visibleSettingsOptions = orderedSettingsOptions.filter(option => can(option.permission));
    useEffect(() => {
        if (open_settings && !visibleSettingsOptions.some(option => option.id === selectedOption)) {
            setSelectedOption(visibleSettingsOptions[0]?.id ?? null);
        }
    }, [open_settings, selectedOption, visibleSettingsOptions]);
    const [loading, setLoading] = useState(false);
    const [watermarkEnabled, setWatermarkEnabled] = useState(false);

    const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    // The full object as returned by GET /round_robin/settings — feature,
    // timerange, holiday_mode, delay, lastAssignedOp, users — held and
    // round-tripped as one unit, since POST /round_robin/settings expects
    // the whole thing back with only the user's edits applied. `lastAssignedOp`
    // is explicitly backend-owned/read-only per the API contract — shown
    // (matched against `users` for a name) but never edited; it just passes
    // through unmodified on save. null until the first load completes.
    const [rrSettings, setRrSettings] = useState(null);

    const load_round_robin_settings = async () => {
        if (!can("settings.sales_assignment.manage")) return;
        try {
            const res = await axios.get(API_DOMAIN + "/round_robin/settings");
            if (res.data.success) setRrSettings(res.data.data);
            else WarningSnackbar("Failed to load Round-Robin settings");
        } catch (error) {
            WarningSnackbar("Failed to load Round-Robin settings! Try again later\n", error.message);
        }
    };

    const toggleDayClosed = (day) => {
        setRrSettings(prev => {
            const isOpen = (prev.timerange[day] || []).length > 0;
            return {
                ...prev,
                timerange: {
                    ...prev.timerange,
                    [day]: isOpen ? [] : [{ from: { hour: 9, minute: 0 }, to: { hour: 18, minute: 0 } }],
                },
            };
        });
    };

    const addSlot = (day) => {
        setRrSettings(prev => ({
            ...prev,
            timerange: {
                ...prev.timerange,
                [day]: [...(prev.timerange[day] || []), { from: { hour: 9, minute: 0 }, to: { hour: 18, minute: 0 } }]
            }
        }));
    };

    const removeSlot = (day, index) => {
        setRrSettings(prev => ({
            ...prev,
            timerange: {
                ...prev.timerange,
                [day]: prev.timerange[day].filter((_, i) => i !== index)
            }
        }));
    };

    const updateSlotTime = (day, slotIndex, which, dayjsVal) => {
        if (!dayjsVal) return;
        setRrSettings(prev => ({
            ...prev,
            timerange: {
                ...prev.timerange,
                [day]: prev.timerange[day].map((slot, i) =>
                    i === slotIndex ? { ...slot, [which]: { hour: dayjsVal.hour(), minute: dayjsVal.minute() } } : slot
                )
            }
        }));
    };

    const [featureVisibility, setFeatureVisibility] = useState({});
    const load_feature_visibility = async () => {
        if (!can("settings.feature_visibility.manage")) return;
        setFeatureVisibility(await fetchFeatureVisibility());
    };
    const set_feature_access = (key, access) => {
        setFeatureVisibility(prev => ({ ...prev, [key]: { ...prev[key], access, userIds: access === 'users' ? (prev[key]?.userIds || []) : undefined } }));
    };
    const set_feature_user_ids = (key, userIds) => {
        setFeatureVisibility(prev => ({ ...prev, [key]: { ...prev[key], access: 'users', userIds } }));
    };
    const save_feature_visibility = async () => {
        if (!can("settings.feature_visibility.manage")) return true;
        try {
            const ok = await saveFeatureVisibility(featureVisibility);
            if (!ok) ErrorSnackbar("Failed to save feature visibility");
            return ok;
        } catch (error) {
            ErrorSnackbar(error.message);
            return false;
        }
    };

    // Which AI provider/model/prompt image-attachment analysis (extraction)
    // uses — separate from chat providers (that's picked per-chat by
    // whoever's chatting), this one config applies globally. Keeps whatever
    // else is in the JSON (timeout_ms, is_active) untouched on save; only
    // provider_id/model/system_prompt are user-editable here. No separate
    // model picker — each ai_providers row is one connection profile with
    // one default_model (same as the chat-creation provider picker), so
    // picking a provider already determines the model; it's just carried
    // into the JSON alongside provider_id rather than shown as its own field.
    const [aiMediaConfig, setAiMediaConfig] = useState(null);
    const [aiProviders, setAiProviders] = useState([]);

    const load_ai_media_analysis_config = async () => {
        if (!can("settings.ai_media_analysis.manage")) return;
        try {
            const [configRes, providersRes] = await Promise.all([
                axios.post(API_DOMAIN + "/whatsapp/get_misc_mode", { mode: AI_MEDIA_ANALYSIS_MODE }),
                axios.get(API_DOMAIN + "/ai/providers"),
            ]);
            if (configRes.data.success) setAiMediaConfig(configRes.data.data || {});
            else WarningSnackbar("Failed to get AI Image Analysis config! Try again later\n");
            if (providersRes.data.success) setAiProviders((providersRes.data.data || []).filter(p => p.is_active));
        } catch (error) {
            WarningSnackbar("Failed to get AI Image Analysis config! Try again later\n", error.message);
        }
    };

    const set_ai_media_provider = (providerId) => {
        const provider = aiProviders.find(p => p.id === providerId);
        setAiMediaConfig(prev => ({ ...prev, provider_id: providerId, model: provider?.default_model || "" }));
    };
    const set_ai_media_system_prompt = (system_prompt) => setAiMediaConfig(prev => ({ ...prev, system_prompt }));

    const save_ai_media_analysis_config = async () => {
        if (!can("settings.ai_media_analysis.manage")) return true;
        if (!aiMediaConfig) return true; // tab never opened/loaded — nothing changed
        try {
            const res = await axios.post(API_DOMAIN + "/whatsapp/save_misc_mode", { mode: AI_MEDIA_ANALYSIS_MODE, moredetails: aiMediaConfig });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message);
            return false;
        }
    };

    const [users, setUsers] = useState([]);
    const load_all_users = async () => {
        if (!can("settings.approvals.manage") && !can("settings.feature_visibility.manage")) return;
        try {
            var response = await axios.get(API_DOMAIN + "/auth/get_users_for_settings");
            if (response.data.success) {
                setUsers(response.data.data);
            } else {
                WarningSnackbar("Failed to get Operators Info! Try again later\n");
            }
        } catch (error) {
            WarningSnackbar("Failed to get Operators Info! Try again later\n", error.message);
        }
    };

    const toggle_operator_assignable = (id, checkValue) => {
        setRrSettings(prev => ({
            ...prev,
            users: prev.users.map(u => u.id === id ? { ...u, rr_assignable: checkValue } : u),
        }));
    };

    // This endpoint is Operations-only and returns only a { users } envelope.
    // The defensive UI filter remains useful, but backend department filtering
    // is authoritative and no WATI scheduler fields are expected here.
    const [opsRrUsers, setOpsRrUsers] = useState(null);

    const load_operations_round_robin_settings = async () => {
        if (!can("settings.operations_assignment.manage")) return;
        try {
            const res = await axios.get(API_DOMAIN + "/round_robin/settings/users");
            if (res.data.success) {
                const opsUsers = (res.data.data.users || []).filter(u => u.department === 6 || u.department_name === 'Operations');
                setOpsRrUsers(opsUsers);
            } else WarningSnackbar("Failed to load Operations Round-Robin settings");
        } catch (error) {
            WarningSnackbar("Failed to load Operations Round-Robin settings! Try again later\n", error.message);
        }
    };

    const toggle_operations_operator_assignable = (id, checkValue) => {
        setOpsRrUsers(prev => prev.map(u => u.id === id ? { ...u, rr_assignable: checkValue } : u));
    };

    const save_operations_round_robin_settings = async () => {
        if (!can("settings.operations_assignment.manage")) return true;
        if (!opsRrUsers) return true; // tab never opened/loaded — nothing changed
        try {
            const res = await axios.post(API_DOMAIN + "/round_robin/settings/users", {
                users: opsRrUsers.map(u => ({ id: u.id, rr_assignable: u.rr_assignable })),
            });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message);
            return false;
        }
    };

    useEffect(() => {
        if (open_settings) {
            if (can("settings.sales_assignment.manage")) load_round_robin_settings();
            if (can("settings.operations_assignment.manage")) load_operations_round_robin_settings();
            if (can("settings.approvals.manage") || can("settings.feature_visibility.manage")) load_all_users();
            if (can("settings.feature_visibility.manage")) load_feature_visibility();
            if (can("settings.ai_media_analysis.manage")) load_ai_media_analysis_config();
        }
    }, [open_settings])

    const handleMenuClick = (id) => {
        setSelectedOption(id);
    };

    const handleWatermarkToggle = (e) => {
        setWatermarkEnabled(e)
    };

    //final save
    const save_settings = async () => {
        setLoading(true)
        try {
            const results = await Promise.all([
                save_round_robin_settings(),
                save_operations_round_robin_settings(),
                save_feature_visibility(),
                save_ai_media_analysis_config(),
            ]);
            if (results.every(Boolean)) {
                handleCloseSettingsModal();
                SuccessSnackbar("Saved Settings Successfully!")
            } else {
                ErrorSnackbar("Some settings failed to save — check the errors above and try again.")
            }
        } catch (error) {
            ErrorSnackbar("Saving settings failed!")
        }
        finally {
            setLoading(false)
        }
    }

    const handleSave = () => {
        if (window.confirm("Are you sure to save changes you made in the settings?")) {
            save_settings()
        }
    };

    const save_round_robin_settings = async () => {
        if (!can("settings.sales_assignment.manage")) return true;
        if (!rrSettings) return true; // tab never opened/loaded — nothing changed
        // The idle-notice threshold has to be at least as long as the
        // assignment delay itself — a threshold shorter than the delay
        // would try to notice-then-assign a chat before it's even eligible
        // for assignment in the first place. Checked here (not just clamped
        // in the input) since editing delay upward on the Operators tab
        // after already setting a lower threshold on the Notice tab would
        // otherwise slip an inconsistent pair through to save.
        const threshold = rrSettings.preAssignNotice?.thresholdMinutes;
        if (threshold != null && threshold < (rrSettings.delay || 0)) {
            ErrorSnackbar(`Idle Threshold (${threshold} min) can't be less than the Assignment Delay (${rrSettings.delay} min).`);
            return false;
        }
        try {
            const res = await axios.post(API_DOMAIN + "/round_robin/settings", {
                ...rrSettings,
                // Only id + rr_assignable matter to the backend update — the
                // rest of each user object (name/department/user_type) is
                // display-only, fetched fresh from GET, so it isn't round-tripped.
                users: rrSettings.users.map(u => ({ id: u.id, rr_assignable: u.rr_assignable })),
            });
            if (!res.data.success) ErrorSnackbar(res.data.message);
            return !!res.data.success;
        } catch (error) {
            ErrorSnackbar(error.message)
            return false;
        }
    }

    const renderDetails = () => {
        switch (selectedOption) {
            case 2: {
                const rr = rrSettings || {};
                const rrUsers = rr.users || [];
                const rrTimerange = rr.timerange || {};
                const rrPreAssignNotice = rr.preAssignNotice || {};
                return (
                    <div>
                        <PanelHeader title="WATI Automated Round-Robin Ticket Assignment" />
                        <Divider style={{ margin: '8px 0 14px' }} />
                        {rrSettings === null ? (
                            <div className='rounded-xl p-3' style={{ border: `1px solid ${token.colorBorderSecondary}` }}><i>Loading…</i></div>
                        ) : (
                        <Tabs
                            size="small"
                            items={[
                                {
                                    key: 'operators',
                                    label: 'Operators',
                                    children: (
                                        <div>
                                            <div className='rounded-xl p-3 flex justify-between items-center' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div>Toggle Auto-Assign On/Off</div>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Switch: {
                                                                handleBg: "#14b840",
                                                                colorPrimary: "#63db83",
                                                                colorPrimaryHover: "#408f55",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Switch
                                                        checkedChildren="On"
                                                        unCheckedChildren="Off"
                                                        value={!!rr.feature?.active}
                                                        onChange={(val) => setRrSettings(prev => ({ ...prev, feature: { ...prev.feature, active: val } }))}
                                                    />
                                                </ConfigProvider>
                                            </div>
                                            <div className='rounded-xl p-3 flex justify-between items-center mt-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div>Assignment Delay <Typography.Text type="secondary" style={{ fontSize: 11 }}>(minutes a new unassigned chat waits before round-robin picks it up)</Typography.Text></div>
                                                <InputNumber
                                                    size="small"
                                                    min={0}
                                                    value={rr.delay}
                                                    onChange={(val) => setRrSettings(prev => ({ ...prev, delay: val ?? 0 }))}
                                                    addonAfter="min"
                                                    style={{ width: 110 }}
                                                />
                                            </div>
                                            <div className='rounded-xl p-3 flex justify-between items-center mt-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div>Last Assigned Operator</div>
                                                <Typography.Text>
                                                    {rrUsers.find(u => u.id === rr.lastAssignedOp)?.name || (rr.lastAssignedOp ?? <i>none yet</i>)}
                                                </Typography.Text>
                                            </div>
                                            <div className='mt-3 mb-2' style={{ fontWeight: 600, fontSize: 13 }}>Set Assignability of Operators</div>
                                            <div className='rounded-xl p-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div className='mt-1'>
                                                    {rrUsers.length ? (
                                                        rrUsers.map((element) => (
                                                            <div className='rounded-lg p-2 m-1 flex justify-between items-center' style={{ border: `1px solid ${token.colorBorderSecondary}` }} key={element.id}>
                                                                <div>
                                                                    {element.name}
                                                                    {element.user_type && <Tag style={{ marginLeft: 6 }}>{element.user_type}</Tag>}
                                                                </div>
                                                                <Switch
                                                                    checkedChildren="On"
                                                                    unCheckedChildren="Off"
                                                                    value={element.rr_assignable}
                                                                    onChange={(e) => toggle_operator_assignable(element.id, e)}
                                                                />
                                                            </div>
                                                        ))
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className='flex justify-center pt-2'>
                                                <Typography.Text type="secondary" style={{ fontSize: 11 }}>Total agents : {rrUsers.length}</Typography.Text>
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'timings',
                                    label: 'Timings',
                                    children: (
                                        <div>
                                            <div className='rounded-xl p-3 flex justify-between items-center mb-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div>Holiday Mode <Typography.Text type="secondary" style={{ fontSize: 11 }}>(disables all auto-assignments)</Typography.Text></div>
                                                <Switch
                                                    checkedChildren="Holiday"
                                                    unCheckedChildren="Active"
                                                    checked={!!rr.holiday_mode}
                                                    onChange={(val) => setRrSettings(prev => ({ ...prev, holiday_mode: val }))}
                                                />
                                            </div>
                                            <div className='rounded-xl p-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                {DAYS.map(day => {
                                                    const slots = rrTimerange[day] || [];
                                                    const isOpen = slots.length > 0;
                                                    return (
                                                        <div key={day} className='rounded-lg p-2 mb-1' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                            <div className='flex justify-between items-center mb-1'>
                                                                <span style={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 80 }}>{day}</span>
                                                                <Switch
                                                                    size="small"
                                                                    checkedChildren="Open"
                                                                    unCheckedChildren="Closed"
                                                                    checked={isOpen}
                                                                    onChange={() => toggleDayClosed(day)}
                                                                />
                                                            </div>
                                                            {isOpen && (
                                                                <div>
                                                                    {slots.map((slot, idx) => (
                                                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                                            <TimePicker
                                                                                size="small"
                                                                                format="HH:mm"
                                                                                minuteStep={15}
                                                                                value={dayjs().hour(slot.from.hour).minute(slot.from.minute).second(0)}
                                                                                onChange={(val) => updateSlotTime(day, idx, 'from', val)}
                                                                                allowClear={false}
                                                                            />
                                                                            <span style={{ fontSize: 11, color: "gray" }}>to</span>
                                                                            <TimePicker
                                                                                size="small"
                                                                                format="HH:mm"
                                                                                minuteStep={15}
                                                                                value={dayjs().hour(slot.to.hour).minute(slot.to.minute).second(0)}
                                                                                onChange={(val) => updateSlotTime(day, idx, 'to', val)}
                                                                                allowClear={false}
                                                                            />
                                                                            <DeleteOutlined
                                                                                style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                                                                onClick={() => removeSlot(day, idx)}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                    <Button
                                                                        size="small"
                                                                        type="dashed"
                                                                        icon={<PlusOutlined />}
                                                                        onClick={() => addSlot(day)}
                                                                    >
                                                                        Add Slot
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                                                Timings save together with the rest of this panel — use the modal's Save Changes button below.
                                            </Typography.Text>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'notice',
                                    label: 'Assignment Notice',
                                    children: (
                                        <div>
                                            <div className='rounded-xl p-3 flex justify-between items-center' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div>Send Notice Before Assigning Idle Chats</div>
                                                <Switch
                                                    checkedChildren="On"
                                                    unCheckedChildren="Off"
                                                    value={!!rrPreAssignNotice.enabled}
                                                    onChange={(val) => setRrSettings(prev => ({ ...prev, preAssignNotice: { ...prev.preAssignNotice, enabled: val } }))}
                                                />
                                            </div>
                                            <div className='rounded-xl p-3 flex justify-between items-center mt-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                                <div>
                                                    Idle Threshold <Typography.Text type="secondary" style={{ fontSize: 11 }}>(chats unassigned longer than this get the message; newer ones are just assigned)</Typography.Text>
                                                    <br />
                                                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>Can't be less than the Assignment Delay ({rr.delay ?? 0} min).</Typography.Text>
                                                </div>
                                                <InputNumber
                                                    size="small"
                                                    min={rr.delay || 0}
                                                    value={rrPreAssignNotice.thresholdMinutes}
                                                    onChange={(val) => setRrSettings(prev => ({ ...prev, preAssignNotice: { ...prev.preAssignNotice, thresholdMinutes: Math.max(val ?? 0, prev.delay || 0) } }))}
                                                    addonAfter="min"
                                                    style={{ width: 110 }}
                                                />
                                            </div>
                                            <div className='mt-3 mb-2' style={{ fontWeight: 600, fontSize: 13 }}>Message</div>
                                            <Input.TextArea
                                                rows={4}
                                                value={rrPreAssignNotice.message || ""}
                                                onChange={(e) => setRrSettings(prev => ({ ...prev, preAssignNotice: { ...prev.preAssignNotice, message: e.target.value } }))}
                                                placeholder="e.g. Thanks for reaching out — an agent will be with you shortly."
                                            />
                                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                                                Sent as a plain WhatsApp text message, not a template — fine for same-day idle chats (the normal case), but chats older than WhatsApp's 24-hour reply window may fail to receive it.
                                            </Typography.Text>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                        )}
                    </div>
                );
            }
            case 3:
                return (
                    <div>
                        <PanelHeader title="Watermark Settings" />
                        <Divider style={{ margin: '8px 0 14px' }} />

                        <div className='rounded-xl p-3' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                            <div className='flex justify-between items-center'>
                                <div>Set Photo Watermark</div>
                                <Tooltip title="Turn the feature on/off">
                                    <Switch
                                        checkedChildren="On"
                                        unCheckedChildren="Off"
                                        value={watermarkEnabled}
                                        onChange={handleWatermarkToggle}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </div>)
            case 7:
                return (<div>
                    <PanelHeader title="Approval Settings" />
                    <Divider style={{ margin: '8px 0 14px' }} />
                    <ApprovalSettingsTab users={users} />
                </div>)
            case 8:
                return (<div>
                    <PanelHeader title="Chat Settings" />
                    <Divider style={{ margin: '8px 0 14px' }} />
                    <ChatSettingsPanel authState={authState} />
                </div>)
            case 9:
                return (<div>
                    <PanelHeader title="Update Notification" />
                    <Divider style={{ margin: '8px 0 14px' }} />
                    <UpdateNotificationPanel />
                </div>)
            case 10:
                return (<div>
                    <PanelHeader title="Feature Visibility" />
                    <Divider style={{ margin: '8px 0 10px' }} />
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                        Controls which buttons/sections show up on the Sales Pipeline page — applies to
                        everyone, admins included. A feature not yet set here stays hidden until you grant it below.
                    </Typography.Text>
                    <div className='rounded-xl p-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                        {FEATURE_KEYS.map(({ key, label }) => {
                            const entry = featureVisibility[key] || {};
                            return (
                                <div key={key} className='rounded-lg p-2 mb-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                    <div className='flex justify-between items-center mb-2'>
                                        <div style={{ fontWeight: 600 }}>{label}</div>
                                        <Select
                                            style={{ width: 180 }}
                                            value={entry.access || undefined}
                                            placeholder="Hidden (not set)"
                                            onChange={(val) => set_feature_access(key, val)}
                                            options={[
                                                { value: 'all', label: 'All Users' },
                                                { value: 'admins', label: 'Admins Only' },
                                                { value: 'users', label: 'Specific Users' },
                                            ]}
                                        />
                                    </div>
                                    {entry.access === 'users' && (
                                        <Select
                                            mode="multiple"
                                            style={{ width: '100%' }}
                                            placeholder="Select users"
                                            value={entry.userIds || []}
                                            onChange={(val) => set_feature_user_ids(key, val)}
                                            optionFilterProp="label"
                                            options={users.map(u => ({ value: u.id, label: u.name }))}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>)
            case 11: {
                const cfg = aiMediaConfig || {};
                return (<div>
                    <PanelHeader title="AI Image Analysis" />
                    <Divider style={{ margin: '8px 0 10px' }} />
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                        Which AI provider/model/prompt image attachments get analyzed with — applies
                        globally (uzd_misc mode <code>{AI_MEDIA_ANALYSIS_MODE}</code>), separate from
                        whichever provider a user picks for their own chat.
                    </Typography.Text>
                    {aiMediaConfig === null ? (
                        <div className='rounded-xl p-3' style={{ border: `1px solid ${token.colorBorderSecondary}` }}><i>Loading…</i></div>
                    ) : (
                        <div className='rounded-xl p-3' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                            <div className='mb-3'>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Provider</div>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="Select a provider"
                                    value={cfg.provider_id ?? undefined}
                                    onChange={set_ai_media_provider}
                                    optionFilterProp="label"
                                    options={aiProviders.map(p => ({
                                        value: p.id,
                                        label: `${p.name} (${p.provider_type})${p.is_default ? " — default" : ""}`,
                                    }))}
                                />
                                {cfg.provider_id && (
                                    <div style={{ fontSize: 11, color: "gray", marginTop: 4 }}>
                                        Model: <b>{cfg.model || "—"}</b> (the provider's own default_model — not separately editable)
                                    </div>
                                )}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>System prompt</div>
                                <Input.TextArea
                                    rows={5}
                                    value={cfg.system_prompt || ""}
                                    onChange={(e) => set_ai_media_system_prompt(e.target.value)}
                                    placeholder="e.g. Analyze the supplied image. If meaningful text is visible, extract it exactly. Otherwise describe the image. Return only valid JSON using the required response schema."
                                />
                            </div>
                        </div>
                    )}
                </div>);
            }
            case 12:
                return (<div>
                    <PanelHeader title="Waiting Price PDF/Excel Scheduler" />
                    <Divider style={{ margin: '8px 0 14px' }} />
                    <WaitingPriceReportSchedulerPanel />
                </div>)
            case 13:
                return (<div>
                    <PanelHeader title="Zoho Connection" />
                    <Divider style={{ margin: '8px 0 14px' }} />
                    <ZohoConnectionPanel />
                </div>)
            case 14:
                return (<div>
                    <PanelHeader title="Margin Rules" />
                    <Divider style={{ margin: '8px 0 14px' }} />
                    <MarginRulesPanel />
                </div>)
            case 15: {
                return (
                    <div>
                        <PanelHeader title="Operations Round-Robin Assignment" />
                        <Divider style={{ margin: '8px 0 14px' }} />
                        {opsRrUsers === null ? (
                            <div className='rounded-xl p-3' style={{ border: `1px solid ${token.colorBorderSecondary}` }}><i>Loading…</i></div>
                        ) : (
                            <div>
                                <div className='mt-1 mb-2' style={{ fontWeight: 600, fontSize: 13 }}>Set Assignability of Operators</div>
                                <div className='rounded-xl p-2' style={{ border: `1px solid ${token.colorBorderSecondary}` }}>
                                    <div className='mt-1'>
                                        {opsRrUsers.length ? (
                                            opsRrUsers.map((element) => (
                                                <div className='rounded-lg p-2 m-1 flex justify-between items-center' style={{ border: `1px solid ${token.colorBorderSecondary}` }} key={element.id}>
                                                    <div>
                                                        {element.name}
                                                        {element.user_type && <Tag style={{ marginLeft: 6 }}>{element.user_type}</Tag>}
                                                    </div>
                                                    <Switch
                                                        checkedChildren="On"
                                                        unCheckedChildren="Off"
                                                        value={element.rr_assignable}
                                                        onChange={(e) => toggle_operations_operator_assignable(element.id, e)}
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <Typography.Text type="secondary">No Operations users found.</Typography.Text>
                                        )}
                                    </div>
                                </div>
                                <div className='flex justify-center pt-2'>
                                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>Total agents : {opsRrUsers.length}</Typography.Text>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            case 16:
                return <AccessPresetsPanel />;
            default:
                return <div>Keep calm, coffee is being converted to code...</div>;
        }
    };

    return (
        <>
            <Modal
                title="Settings"
                centered
                open={open_settings}
                onCancel={handleCloseSettingsModal}
                maskClosable={false}
                width={MODAL_WIDTH}
                styles={{ content: { borderRadius: 18 }, body: { padding: 0 } }}
                footer={
                    // Always render the footer bar itself (Waiting Price Reports saves
                    // through its own in-pane button, not this one) — swapping the whole
                    // footer prop to null instead of just hiding the button inside it
                    // would drop antd's footer padding/border along with it, shrinking
                    // the modal's total height only for that one pane.
                    <div className="flex justify-end" style={{ minHeight: 22 }}>
                        {selectedOption && ![12, 13, 14, 16].includes(selectedOption) && (
                            <Button icon={<SaveOutlined />} loading={loading} type="primary" onClick={handleSave}>
                                Save Changes
                            </Button>
                        )}
                    </div>
                }
            >
                <div style={{ display: 'flex', height: '480px' }}>
                    {/* Menu Pane */}
                    <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${token.colorBorderSecondary}`, padding: '12px 8px', overflowY: 'auto' }}>
                        <Menu
                            mode="inline"
                            selectedKeys={[String(selectedOption)]}
                            onClick={({ key }) => handleMenuClick(Number(key))}
                            style={{ border: 'none' }}
                            items={visibleSettingsOptions.map(option => ({
                                key: String(option.id),
                                icon: option.icon,
                                label: option.name,
                            }))}
                        />
                    </div>

                    {/* Details Pane */}
                    <div style={{ flex: 1, minWidth: 0, padding: '20px 24px', overflowY: 'auto' }}>
                        {renderDetails()}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default SettingsModal;
